import { Button, Card, Input, Section, Select, useToast } from '@memorizecards/ui';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { IconTrash } from '../components/Icons';
import { VisualizarCardModal } from '../components/VisualizarCardModal';
import { Carregando, Erro, TituloPagina, Vazio } from '../components/estado';
import { addDaysIso, formatNumber, labelDoCaminho, todayIso } from '../lib/format';
import {
  type FavoriteFilters,
  useDecks,
  useFavoritas,
  useToggleFavorita,
} from '../lib/queries';
import type { FavoriteSort } from '../lib/types';
import { Paginacao } from './Caderno';

const SORTS: Array<{ value: FavoriteSort; label: string }> = [
  { value: 'recent', label: 'Mais recentes' },
  { value: 'oldest', label: 'Mais antigas' },
  { value: 'most_errors', label: 'Mais erradas' },
  { value: 'last_error', label: 'Última resposta errada' },
  { value: 'pacote', label: 'Por pacote' },
  { value: 'materia', label: 'Por matéria' },
];

function lerFiltros(params: URLSearchParams, page: number): FavoriteFilters {
  return {
    page,
    deckId: Number(params.get('deck') ?? 0) || null,
    sort: (params.get('sort') as FavoriteSort) || 'recent',
    pacote: params.get('pacote'),
    subtopic: params.get('assunto'),
    desde: params.get('desde'),
    ate: params.get('ate'),
    ultimasErradas: params.get('erradas') === '1',
  };
}

function montarUrlEstudo(filtros: Omit<FavoriteFilters, 'page' | 'sort'>): string {
  const p = new URLSearchParams();
  p.set('favoritas', '1');
  if (filtros.deckId) p.set('deck', String(filtros.deckId));
  if (filtros.pacote) p.set('pacote', filtros.pacote);
  if (filtros.subtopic) p.set('assunto', filtros.subtopic);
  if (filtros.desde) p.set('desde', filtros.desde);
  if (filtros.ate) p.set('ate', filtros.ate);
  if (filtros.ultimasErradas) p.set('erradas', '1');
  return `/estudar?${p.toString()}`;
}

export function Favoritas(): ReactNode {
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [filtrosAbertos, setFiltrosAbertos] = useState(true);
  const [cardVer, setCardVer] = useState<number | null>(null);

  const filtros = useMemo(() => lerFiltros(params, page), [params, page]);
  const { data: decks } = useDecks();
  const { data, isPending, error, refetch } = useFavoritas(filtros);
  const alternar = useToggleFavorita();
  const toast = useToast();

  const materiasFiltradas = useMemo(() => {
    const base = (decks ?? []).filter((d) => d.favoritas > 0);
    if (!filtros.pacote) return base;
    return base.filter((d) => d.pacote === filtros.pacote);
  }, [decks, filtros.pacote]);

  function atualizar(chave: string, valor: string | null) {
    const proximos = new URLSearchParams(params);
    if (valor && valor !== '') proximos.set(chave, valor);
    else proximos.delete(chave);
    setParams(proximos, { replace: true });
    setPage(1);
  }

  function aplicarPeriodo(dias: number) {
    const proximos = new URLSearchParams(params);
    proximos.set('desde', addDaysIso(todayIso(), -dias));
    proximos.set('ate', todayIso());
    proximos.delete('erradas');
    setParams(proximos, { replace: true });
    setPage(1);
  }

  function atalhoUltimasErradas() {
    const proximos = new URLSearchParams(params);
    proximos.set('erradas', '1');
    proximos.set('sort', 'last_error');
    proximos.delete('desde');
    proximos.delete('ate');
    setParams(proximos, { replace: true });
    setPage(1);
  }

  function atalhoMaisErradas() {
    const proximos = new URLSearchParams(params);
    proximos.set('sort', 'most_errors');
    proximos.delete('erradas');
    setParams(proximos, { replace: true });
    setPage(1);
  }

  function limparFiltros() {
    setParams(new URLSearchParams(), { replace: true });
    setPage(1);
  }

  const temFiltros =
    filtros.deckId ||
    filtros.pacote ||
    filtros.subtopic ||
    filtros.desde ||
    filtros.ate ||
    filtros.ultimasErradas ||
    filtros.sort !== 'recent';

  return (
    <Section spacing="md">
      <TituloPagina
        eyebrow="Marcadas por você"
        titulo="Favoritas"
        descricao="Os cards que você marcou com a estrela. Filtre, visualize e estude a seleção."
      />

      <Card padding="md" className="mb-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-medium text-mc-ink">Filtros</h2>
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => setFiltrosAbertos((v) => !v)}>
              {filtrosAbertos ? 'Ocultar' : 'Mostrar'}
            </Button>
            {temFiltros ? (
              <Button variant="ghost" size="sm" onClick={limparFiltros}>
                Limpar
              </Button>
            ) : null}
          </div>
        </div>

        {filtrosAbertos ? (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Select
              label="Ordenação"
              value={filtros.sort}
              onChange={(e) => atualizar('sort', e.target.value === 'recent' ? null : e.target.value)}
            >
              {SORTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </Select>

            <Select
              label="Pacote"
              value={filtros.pacote ?? ''}
              onChange={(e) => {
                atualizar('pacote', e.target.value || null);
                if (filtros.deckId) atualizar('deck', null);
              }}
            >
              <option value="">Todos os pacotes</option>
              {(data?.pacotes ?? []).map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>

            <Select
              label="Matéria"
              value={filtros.deckId ? String(filtros.deckId) : ''}
              onChange={(e) => atualizar('deck', e.target.value || null)}
            >
              <option value="">Todas as matérias</option>
              {materiasFiltradas.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.pacote} — {d.nome} ({d.favoritas})
                </option>
              ))}
            </Select>

            <Input
              label="Assunto / sub-tópico"
              value={filtros.subtopic ?? ''}
              onChange={(e) => atualizar('assunto', e.target.value || null)}
              placeholder="Buscar no caminho do card…"
            />

            <Input
              label="Desde"
              type="date"
              value={filtros.desde ?? ''}
              onChange={(e) => atualizar('desde', e.target.value || null)}
            />

            <Input
              label="Até"
              type="date"
              value={filtros.ate ?? ''}
              onChange={(e) => atualizar('ate', e.target.value || null)}
            />
          </div>
        ) : null}

        {filtrosAbertos ? (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => aplicarPeriodo(7)}>
              Últimos 7 dias
            </Button>
            <Button variant="ghost" size="sm" onClick={() => aplicarPeriodo(30)}>
              Últimos 30 dias
            </Button>
            <Button variant="ghost" size="sm" onClick={() => aplicarPeriodo(90)}>
              Últimos 90 dias
            </Button>
            <Button variant="ghost" size="sm" onClick={atalhoUltimasErradas}>
              Últimas erradas
            </Button>
            <Button variant="ghost" size="sm" onClick={atalhoMaisErradas}>
              Mais erradas
            </Button>
          </div>
        ) : null}
      </Card>

      {isPending ? <Carregando /> : null}
      {error ? <Erro error={error} aoTentarNovamente={() => void refetch()} /> : null}

      {data && data.total === 0 ? (
        <Vazio
          titulo="Nenhuma favorita encontrada"
          descricao={
            temFiltros
              ? 'Nenhum card corresponde aos filtros. Tente ampliar a busca.'
              : 'Clique na estrela durante o estudo para guardar um card aqui.'
          }
          acao={
            temFiltros ? (
              <Button variant="ghost" onClick={limparFiltros}>
                Limpar filtros
              </Button>
            ) : (
              <Link to="/baralhos">
                <Button>Ir estudar</Button>
              </Link>
            )
          }
        />
      ) : null}

      {data && data.total > 0 ? (
        <div className="flex flex-col gap-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="mc-mono">
              {formatNumber(data.total)} {data.total === 1 ? 'card' : 'cards'}
            </p>
            <Link to={montarUrlEstudo(filtros)}>
              <Button>Estudar seleção ({formatNumber(data.total)})</Button>
            </Link>
          </div>

          <ul className="flex flex-col gap-3">
            {data.itens.map((item) => (
              <li key={item.cardId}>
                <Card padding="md" className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-mc-ink3">
                      {item.pacote ?? '—'} · {item.materia ?? '—'}
                      {item.erros > 0 ? ` · ${item.erros} erro(s)` : null}
                    </p>
                    <p className="mt-1 text-sm text-mc-ink">{labelDoCaminho(item.frente)}</p>
                    <div className="mt-2 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={() => setCardVer(item.cardId)}
                        className="text-sm text-mc-accent underline underline-offset-4"
                      >
                        Ver card
                      </button>
                      <Link
                        to={`/estudar?deck=${item.deckId}`}
                        className="text-sm text-mc-accent underline underline-offset-4"
                      >
                        Estudar matéria
                      </Link>
                    </div>
                  </div>
                  <button
                    type="button"
                    aria-label="Remover das favoritas"
                    onClick={() =>
                      alternar.mutate(
                        { cardId: item.cardId },
                        {
                          onSuccess: () => toast.success('Removida das favoritas.'),
                          onError: () => toast.error('Não consegui remover.'),
                        },
                      )
                    }
                    className="grid size-11 shrink-0 place-items-center rounded-mc-pill border border-mc-danger/50 text-mc-danger transition-colors hover:bg-mc-danger/10"
                  >
                    <IconTrash className="size-4" />
                  </button>
                </Card>
              </li>
            ))}
          </ul>

          <Paginacao
            pagina={page}
            paginas={Math.ceil(data.total / 25)}
            aoMudar={setPage}
          />
        </div>
      ) : null}

      <VisualizarCardModal
        cardId={cardVer}
        open={cardVer !== null}
        onClose={() => setCardVer(null)}
      />
    </Section>
  );
}
